/* Invoice detail drawer — slides in from the right when an invoice
 * row is clicked on the Invoices index.
 *
 * Loads /api/merchant/invoices/:id, shows the full record + line
 * items + payment status + actions (resend, mark paid, void, copy
 * payment link, open public preview).
 *
 * Actions allowed by status:
 *   DRAFT    → send (DRAFT → SENT), mark paid, void, delete
 *   SENT     → resend, mark paid, void
 *   OVERDUE  → resend, mark paid, void
 *   PARTIAL  → mark paid (collect remainder)
 *   PAID     → no destructive actions (immutable)
 *   VOID     → no actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Send, CheckCircle2, Ban, Trash2, Copy, ExternalLink, Loader2 } from 'lucide-react';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useCurrency } from '../../contexts/CurrencyContext';
import './MerchantInvoiceDrawer.css';

/* Small inline modal for the manual mark-paid flow. Collects an
   amount (defaults to remaining balance) + an optional reference
   note. Lives at module scope per the no-inline-components rule. */
function MarkPaidModal({ invoice, defaultAmount, onCancel, onSubmit, busy }) {
  const [amount, setAmount] = useState(String(defaultAmount));
  const [note, setNote]     = useState('');
  const handle = (e) => {
    e.preventDefault();
    const a = Number(amount);
    if (!Number.isFinite(a) || a <= 0) return;
    onSubmit({ amountPaid: a, note: note.trim() });
  };
  return (
    <div className="minvdr-confirm-overlay" onClick={onCancel}>
      <form className="minvdr-confirm" onClick={(e) => e.stopPropagation()} onSubmit={handle}>
        <header>
          <h3>Mark as paid</h3>
          <p>Record a payment received outside the system (cash, transfer, cheque).</p>
        </header>
        <label>
          <span>Amount received ({invoice.currency})</span>
          <input
            type="number" min="0.01" step="0.01" autoFocus
            value={amount} onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label>
          <span>Reference / note (optional)</span>
          <input
            type="text" maxLength={200} placeholder="e.g., Bank transfer ref 12345"
            value={note} onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <footer>
          <button type="button" className="minvdr-btn minvdr-btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="minvdr-btn minvdr-btn--primary" disabled={busy}>
            {busy ? 'Saving…' : 'Mark paid'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function effectiveStatus(inv) {
  if (inv?.status === 'SENT' && inv.dueDate && new Date(inv.dueDate) < new Date()) return 'OVERDUE';
  return inv?.status || 'DRAFT';
}

const STATUS_KIND = {
  DRAFT:   'neutral',
  SENT:    'accrued',
  PARTIAL: 'info',
  PAID:    'paid',
  OVERDUE: 'overdue',
  VOID:    'neutral',
};

export default function MerchantInvoiceDrawer({ invoiceId, onClose, onSaved }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const { formatInDisplayCurrency } = useCurrency();
  const formatMoney = (v) => formatInDisplayCurrency(v ?? 0);

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'send' | 'paid' | 'void' | 'delete'
  const [markPaidOpen, setMarkPaidOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/merchant/invoices/${invoiceId}`);
      setInvoice(data?.invoice || null);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load invoice');
    } finally { setLoading(false); }
  }, [invoiceId, toast]);

  useEffect(() => { load(); }, [load]);

  // Escape closes the drawer — standard for slide-in panels.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSend = useCallback(async () => {
    const ok = await confirm(
      `Send invoice ${invoice.invoiceNumber} to ${invoice.customerEmail}?`,
      { title: 'Send invoice', confirmText: 'Send', type: 'info' },
    );
    if (!ok) return;
    try {
      setBusy('send');
      const { data } = await axios.post(`/merchant/invoices/${invoiceId}/send`);
      toast.success(data?.emailSent ? 'Invoice sent' : 'Send attempted — email failed, please retry');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Send failed');
    } finally { setBusy(null); }
  }, [confirm, invoice, invoiceId, load, toast]);

  const submitMarkPaid = useCallback(async ({ amountPaid, note }) => {
    try {
      setBusy('paid');
      await axios.post(`/merchant/invoices/${invoiceId}/mark-paid`, { amountPaid, note });
      toast.success('Invoice marked paid + wallet credited');
      setMarkPaidOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Mark-paid failed');
    } finally { setBusy(null); }
  }, [invoiceId, onSaved, toast]);

  const handleVoid = useCallback(async () => {
    const ok = await confirm(
      'Void this invoice? This cannot be undone.',
      { title: 'Void invoice', confirmText: 'Void' },
    );
    if (!ok) return;
    try {
      setBusy('void');
      await axios.post(`/merchant/invoices/${invoiceId}/void`);
      toast.success('Invoice voided');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Void failed');
    } finally { setBusy(null); }
  }, [confirm, invoiceId, onSaved, toast]);

  const handleDelete = useCallback(async () => {
    const ok = await confirm(
      'Delete this draft invoice? This cannot be undone.',
      { title: 'Delete draft', confirmText: 'Delete' },
    );
    if (!ok) return;
    try {
      setBusy('delete');
      await axios.delete(`/merchant/invoices/${invoiceId}`);
      toast.success('Draft deleted');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Delete failed');
    } finally { setBusy(null); }
  }, [confirm, invoiceId, onSaved, toast]);

  // Use invoice.paymentLink — backend-persisted, built with
  // INVOICE_PAY_DOMAIN. window.location.origin would point at the
  // merchant's dashboard subdomain, not the customer pay subdomain.
  const copyLink = () => {
    if (!invoice?.paymentToken) return;
    const url = invoice.paymentLink || `${window.location.origin}/invoice/${invoice.paymentToken}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Payment link copied'))
      .catch(() => toast.error('Copy failed'));
  };
  const openPublic = () => {
    if (!invoice?.paymentToken) return;
    const url = invoice.paymentLink || `/invoice/${invoice.paymentToken}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const status = effectiveStatus(invoice);
  const isImmutable = status === 'PAID' || status === 'VOID';
  const canSend     = status === 'DRAFT' || status === 'SENT' || status === 'OVERDUE';
  const canMarkPaid = !isImmutable && status !== 'DRAFT' ? true : status === 'DRAFT';
  const canVoid     = !isImmutable;
  const canDelete   = status === 'DRAFT';

  return (
    <div className="minvdr-overlay" onClick={onClose}>
      <aside className="minvdr-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="minvdr-head">
          <div>
            <div className="minvdr-title">{invoice?.invoiceNumber || 'Invoice'}</div>
            <div className={`minvdr-pill minvdr-pill--${STATUS_KIND[status] || 'neutral'}`}>{status}</div>
          </div>
          <button type="button" className="minvdr-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        {loading ? (
          <div className="minvdr-loading"><Loader2 size={20} className="minvdr-spin" /></div>
        ) : !invoice ? (
          <div className="minvdr-loading">Invoice not found.</div>
        ) : (
          <div className="minvdr-body">
            <section className="minvdr-section">
              <div className="minvdr-section-title">Customer</div>
              <div className="minvdr-kv"><span>Name</span><span>{invoice.customerName}</span></div>
              <div className="minvdr-kv"><span>Email</span><span>{invoice.customerEmail}</span></div>
              {invoice.customerPhone && <div className="minvdr-kv"><span>Phone</span><span>{invoice.customerPhone}</span></div>}
            </section>

            <section className="minvdr-section">
              <div className="minvdr-section-title">Line items</div>
              <ul className="minvdr-items">
                {(invoice.lineItems || []).map((li, i) => (
                  <li key={i}>
                    <div className="minvdr-item-desc">
                      <div>{li.description}</div>
                      <div className="minvdr-muted minvdr-tiny">{li.qty} × {formatMoney(li.unitPrice)}</div>
                    </div>
                    <div className="minvdr-item-total">{formatMoney((Number(li.qty) || 0) * (Number(li.unitPrice) || 0))}</div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="minvdr-totals">
              <div className="minvdr-kv"><span>Subtotal</span><span>{formatMoney(invoice.subtotal)}</span></div>
              {Number(invoice.taxTotal) > 0 && (
                <div className="minvdr-kv"><span>Tax</span><span>{formatMoney(invoice.taxTotal)}</span></div>
              )}
              <div className="minvdr-kv minvdr-kv--grand"><span>Total</span><span>{formatMoney(invoice.total)}</span></div>
              <div className="minvdr-kv"><span>Paid</span><span>{formatMoney(invoice.amountPaid)}</span></div>
              <div className="minvdr-kv"><span>Balance due</span><span>{formatMoney(Math.max(0, Number(invoice.total) - Number(invoice.amountPaid)))}</span></div>
            </section>

            <section className="minvdr-section">
              <div className="minvdr-section-title">Schedule</div>
              <div className="minvdr-kv"><span>Issued</span><span>{fmtDate(invoice.issuedAt)}</span></div>
              <div className="minvdr-kv"><span>Due</span><span>{fmtDate(invoice.dueDate)}</span></div>
              {invoice.paidAt && <div className="minvdr-kv"><span>Paid at</span><span>{fmtDate(invoice.paidAt)}</span></div>}
              {invoice.paymentMethod && <div className="minvdr-kv"><span>Method</span><span>{invoice.paymentMethod}</span></div>}
            </section>

            <section className="minvdr-section">
              <div className="minvdr-section-title">Customer link</div>
              <div className="minvdr-link-actions">
                <button type="button" className="minvdr-btn minvdr-btn--ghost" onClick={copyLink}>
                  <Copy size={14} /> Copy link
                </button>
                <button type="button" className="minvdr-btn minvdr-btn--ghost" onClick={openPublic}>
                  <ExternalLink size={14} /> Preview
                </button>
              </div>
              <div className="minvdr-kv"><span>Views</span><span>{invoice.viewCount || 0}</span></div>
              {invoice.firstViewedAt && <div className="minvdr-kv"><span>First viewed</span><span>{fmtDate(invoice.firstViewedAt)}</span></div>}
            </section>

            {invoice.notes && (
              <section className="minvdr-section">
                <div className="minvdr-section-title">Notes</div>
                <p className="minvdr-notes">{invoice.notes}</p>
              </section>
            )}
          </div>
        )}

        {invoice && (
          <footer className="minvdr-foot">
            {canSend && (
              <button type="button" className="minvdr-btn minvdr-btn--primary" onClick={handleSend} disabled={!!busy}>
                <Send size={14} /> {status === 'DRAFT' ? 'Send invoice' : 'Resend'}
              </button>
            )}
            {canMarkPaid && (
              <button type="button" className="minvdr-btn minvdr-btn--ghost" onClick={() => setMarkPaidOpen(true)} disabled={!!busy}>
                <CheckCircle2 size={14} /> Mark paid
              </button>
            )}
            {canVoid && (
              <button type="button" className="minvdr-btn minvdr-btn--danger" onClick={handleVoid} disabled={!!busy}>
                <Ban size={14} /> Void
              </button>
            )}
            {canDelete && (
              <button type="button" className="minvdr-btn minvdr-btn--ghost" onClick={handleDelete} disabled={!!busy}>
                <Trash2 size={14} /> Delete draft
              </button>
            )}
          </footer>
        )}
      </aside>

      {markPaidOpen && invoice && (
        <MarkPaidModal
          invoice={invoice}
          defaultAmount={Math.max(0, Number(invoice.total) - Number(invoice.amountPaid)).toFixed(2)}
          onCancel={() => setMarkPaidOpen(false)}
          onSubmit={submitMarkPaid}
          busy={busy === 'paid'}
        />
      )}
    </div>
  );
}
